import { IconButton, TextField, Box, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import React, { useEffect, useState } from "react";
import { RichTreeView } from "@mui/x-tree-view";
import { useDispatch, useSelector } from "react-redux";
import {
  setCICustomManageForecastBottlerData,
  setCICustomManageForecastFinalTreeData,
  setCICustomManageForecastTreeData,
} from "../../../redux/CICustomManageScreenRedux";
import { useCICustomBottlerSelection } from "../../../context/CICustomBottlerSelectionContext";
import ApiService from "../../../utils/fetch";

const CICustomBottlerSelectionAccordion = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTreeData, SetFilteredTreeData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [treeData, setTreeData] = useState([]);

  const { setCustomSelectedData } = useCICustomBottlerSelection();

  //Redux
  const dispatch = useDispatch();

  const ciManageForecastTreeDataRedux = useSelector(
    (state) => state.CICustomManageScreenRedux.ciCustomManageForecastTreeData,
  );

  //Transform Data
  const transformData = (data) => {
    return data.map((bottlerItem, bottlerIndex) => ({
      id: `bottler${bottlerIndex + 1}`,
      label: `${bottlerItem.bottler} - ${bottlerItem.bottlerOwnerCode}${
        bottlerItem.bottlerOwnerDescription
          ? ` - ${bottlerItem.bottlerOwnerDescription}`
          : ""
      }`,
      bottler: bottlerItem.bottler,
      bottlerOwnerCode: bottlerItem.bottlerOwnerCode,
      children: (bottlerItem.profitCenterDescription || []).map(
        (pcDesc, countryIndex) => ({
          id: `pcCountry${bottlerIndex + 1}-${countryIndex + 1}`,
          label: pcDesc,
          profitCenterCountry: pcDesc,
        }),
      ),
    }));
  };

  // Refresh data from API
  const refreshData = async () => {
    try {
      const response = await ApiService.get(
        "/v1/custom-conc-inventory/custom-bottler-selection",
      );

      const transformedData = transformData(response.data);
      setTreeData(transformedData);
      SetFilteredTreeData(transformedData);

      const allIds = getAllIds(transformedData);
      dispatch(setCICustomManageForecastTreeData(transformedData));

      const filteredItems = allIds.filter((item) =>
        item.startsWith("pcCountry"),
      );
      const ApplyTreeViewData = filterBottlersByPcCountry(
        filteredItems,
        transformedData,
      );
      dispatch(setCICustomManageForecastFinalTreeData(ApplyTreeViewData));
    } catch (error) {
      console.error("Error fetching custom bottler data:", error);
    }
  };

  //Fetch Data & Transform Data
  useEffect(() => {
    if (
      ciManageForecastTreeDataRedux === null ||
      ciManageForecastTreeDataRedux.length === 0
    ) {
      refreshData();
    } else {
      setTreeData(ciManageForecastTreeDataRedux);
      SetFilteredTreeData(ciManageForecastTreeDataRedux);
    }
  }, []);

  //get All Ids from Tree Data
  const getAllIds = (items) => {
    const ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getAllIds(item.children));
      }
    });
    return ids;
  };

  //Event handler for when selected items change
  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(
      filteredTreeData,
      ids,
      selectedIds,
    );

    setSelectedIds(newSelectedIds);

    const filteredItems = newSelectedIds.filter((item) =>
      item.startsWith("pcCountry"),
    );
    console.log("Selected IDs:", newSelectedIds);

    const ApplyTreeViewData = filterBottlersByPcCountry(
      filteredItems,
      treeData,
    );

    const bottler = getSelectedBottler(newSelectedIds, treeData);
    dispatch(setCICustomManageForecastBottlerData(bottler));
    setCustomSelectedData(ApplyTreeViewData);
  };

  const getSelectedBottler = (selectedIds, treeData) => {
    if (!selectedIds?.length) return null;

    const bottlerId = selectedIds.find((id) => id.startsWith("bottler"));
    if (bottlerId) {
      const node = treeData.find((b) => b.id === bottlerId);
      if (!node) return null;

      return {
        bottler: node.bottler,
        bottlerOwnerCode: node.bottlerOwnerCode,
      };
    }

    const pcCountryId = selectedIds.find((id) => id.startsWith("pcCountry"));
    if (!pcCountryId) return null;

    for (const node of treeData) {
      if (node.children?.some((c) => c.id === pcCountryId)) {
        return {
          bottler: node.bottler,
          bottlerOwnerCode: node.bottlerOwnerCode,
        };
      }
    }

    return null;
  };

  //Filter Bottlers By PcCountry Ids
  const filterBottlersByPcCountry = (pcCountryIds, transformedData) => {
    return transformedData.flatMap((bottler) =>
      bottler.children
        .filter((pc) => pcCountryIds.includes(pc.id))
        .map((pc) => ({
          profitCenterCountry: pc.profitCenterCountry,
          bottler: bottler.bottler,
          bottlerOwnerCode: bottler.bottlerOwnerCode,
        })),
    );
  };

  //Function to determine which IDs should be set based on the current and new selections
  function determineIdsToSet(items, newIds, currentIds) {
    const isDeselectingNode = currentIds.length > newIds.length;
    if (isDeselectingNode) {
      const removed = currentIds.filter((id) => !newIds.includes(id))[0];
      const parentIdsToRemove = getAllParentIds(items, removed);
      const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);
      const newIdsWithParentsAndChildrenRemoved = newIds.filter(
        (id) =>
          !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id),
      );
      return newIdsWithParentsAndChildrenRemoved;
    }
    const added = newIds.filter((id) => !currentIds.includes(id))[0];
    const idsToSet = getSelectedIdsAndChildrenIds(items, newIds);
    let parent = getParentNode(items, added);
    while (parent) {
      const childIds = parent.children?.map((node) => node.id) ?? [];
      const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));
      if (allChildrenSelected) {
        idsToSet.push(parent.id);
        parent = getParentNode(items, parent.id);
      } else {
        break;
      }
    }
    return idsToSet;
  }

  // Helper function to get selected IDs along with their children
  function getSelectedIdsAndChildrenIds(items, selectedIds) {
    const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
    for (const item of items) {
      if (selectedIds.includes(item.id)) {
        selectedIdIncludingChildrenIds.add(item.id);
        if (item.children) {
          const childrenIds = item.children.map((child) => child.id);
          const childrenSelectedIds = getSelectedIdsAndChildrenIds(
            item.children,
            childrenIds,
          );
          childrenSelectedIds.forEach((selectedId) =>
            selectedIdIncludingChildrenIds.add(selectedId),
          );
        }
      } else if (item.children) {
        const childrenSelectedIds = getSelectedIdsAndChildrenIds(
          item.children,
          selectedIds,
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId),
        );
      }
    }
    return [...Array.from(selectedIdIncludingChildrenIds)];
  }

  // Helper function to find the parent node of a given item ID
  function getParentNode(items, id) {
    for (const item of items) {
      if (item.children) {
        if (item.children.some((child) => child.id === id)) {
          return item;
        } else {
          const parentNode = getParentNode(item.children, id);
          if (parentNode) {
            return parentNode;
          }
        }
      }
    }
    return undefined;
  }

  // Helper function to get all parent IDs of a given item ID
  function getAllParentIds(items, id) {
    const parentIds = [];
    let parent = getParentNode(items, id);
    while (parent) {
      parentIds.push(parent.id);
      parent = getParentNode(items, parent.id);
    }
    return parentIds;
  }

  //Filter Tree Data
  const filterTreeData = (items, query) => {
    if (!query) {
      return items;
    }
    return items
      .filter((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterTreeData(item.children, query)
          : [];
        return matches || children.length > 0;
      })
      .map((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        // If parent matches query, include ALL children (unfiltered)
        // If parent doesn't match, only include children that match
        const children = matches
          ? item.children
          : item.children
            ? filterTreeData(item.children, query)
            : [];
        return {
          ...item,
          children: children,
        };
      });
  };

  //handle Search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    SetFilteredTreeData(filterTreeData(treeData, event.target.value));
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "75vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: "-20px",
            width: "100%",
          }}
        >
          <Typography gutterBottom style={{ marginBottom: "-10px" }}>
            Bottler Selection *
          </Typography>
          <IconButton
            onClick={refreshData}
            size="small"
            title="Refresh Bottler Selection"
            aria-label="refresh bottler selection"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search"
          margin="normal"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            "& .MuiInputBase-input": {
              padding: "6px 8px",
              fontSize: "0.85rem",
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton size="small">
                <SearchIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, pt: 0 }}>
          <RichTreeView
            multiSelect
            checkboxSelection
            selectedItems={selectedIds}
            onSelectedItemsChange={handleSelectedItemsChange}
            items={filteredTreeData}
            sx={{
              flexGrow: 1,
              backgroundColor: "white",
              "& .MuiTreeItem-label": {
                fontSize: "11px",
              },
            }}
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default CICustomBottlerSelectionAccordion;
